import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PRODUCTS as RAW_PRODUCTS, CATEGORIES } from "@/data/products";
import { invalidateProductsCache } from "@/hooks/useProducts";
import { invalidateSettingsCache } from "@/hooks/useSettings";
import { invalidateCategoryImagesCache, resolveCategoryImage } from "@/hooks/useCategoryImages";
import { photoUrl } from "@/utils/photoUrl";
import {
  Atom,
  LogOut,
  RefreshCw,
  Trash2,
  Lock,
  Mail,
  Phone,
  MapPin,
  Package,
  Loader2,
  ShieldAlert,
  ArrowLeft,
  Inbox,
  Boxes,
  Save,
  RotateCcw,
  Search,
  ImagePlus,
  Plus,
  X,
  FileText,
  Settings as SettingsIcon,
  Layers,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "sl_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [activeTab, setActiveTab] = useState("orders");
  const [overrides, setOverrides] = useState({});
  const [customProducts, setCustomProducts] = useState([]);
  const [pages, setPages] = useState([]);
  const [settings, setSettings] = useState({ whatsapp_supreme: "", whatsapp_gymgear: "" });
  const [categoryImages, setCategoryImages] = useState({});
  const navigate = useNavigate();

  const fetchOrders = async (authToken) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/orders`, {
        headers: { "x-admin-password": authToken },
      });
      setOrders(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        toast.error("Could not fetch orders");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchOverrides = async () => {
    try {
      const res = await axios.get(`${API}/products/overrides`);
      setOverrides(res.data || {});
    } catch {
      // ignore; overrides are optional
    }
  };

  const fetchCustomProducts = async () => {
    try {
      const res = await axios.get(`${API}/products/custom`);
      setCustomProducts(res.data || []);
    } catch {
      // ignore
    }
  };

  const fetchPages = async () => {
    try {
      const res = await axios.get(`${API}/pages`);
      setPages(res.data || []);
    } catch {
      // ignore
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings(res.data || {});
    } catch {
      // ignore
    }
  };

  const fetchCategoryImages = async () => {
    try {
      const res = await axios.get(`${API}/categories/images`);
      setCategoryImages(res.data || {});
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    if (token) {
      fetchOrders(token);
      fetchOverrides();
      fetchCustomProducts();
      fetchPages();
      fetchSettings();
      fetchCategoryImages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const saveProductOverride = async (productId, payload) => {
    try {
      const res = await axios.put(`${API}/admin/products/${productId}`, payload, {
        headers: { "x-admin-password": token },
      });
      setOverrides((prev) => ({ ...prev, [productId]: res.data }));
      invalidateProductsCache();
      toast.success("Product saved");
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save product");
      return false;
    }
  };

  const resetProductOverride = async (productId) => {
    try {
      await axios.delete(`${API}/admin/products/${productId}`, {
        headers: { "x-admin-password": token },
      });
      setOverrides((prev) => {
        const next = { ...prev };
        delete next[productId];
        return next;
      });
      invalidateProductsCache();
      toast.success("Reverted to defaults");
    } catch {
      toast.error("Could not reset product");
    }
  };

  const setProductHidden = async (productId, hidden) => {
    try {
      const existing = overrides[productId] || {};
      const res = await axios.put(
        `${API}/admin/products/${productId}`,
        { ...existing, hidden },
        { headers: { "x-admin-password": token } },
      );
      setOverrides((prev) => ({ ...prev, [productId]: res.data }));
      invalidateProductsCache();
      toast.success(hidden ? "Product hidden from catalog" : "Product visible again");
    } catch {
      toast.error("Could not update visibility");
    }
  };

  const createCustomProduct = async (payload) => {
    try {
      const res = await axios.post(`${API}/admin/products`, payload, {
        headers: { "x-admin-password": token },
      });
      setCustomProducts((prev) => [res.data, ...prev]);
      invalidateProductsCache();
      toast.success("Product created");
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create product");
      return false;
    }
  };

  const updateCustomProduct = async (productId, payload) => {
    try {
      const res = await axios.put(`${API}/admin/products/custom/${productId}`, payload, {
        headers: { "x-admin-password": token },
      });
      setCustomProducts((prev) => prev.map((p) => (p.id === productId ? res.data : p)));
      invalidateProductsCache();
      toast.success("Product updated");
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update product");
      return false;
    }
  };

  const deleteCustomProduct = async (productId) => {
    if (!window.confirm("Delete this custom product permanently?")) return;
    try {
      await axios.delete(`${API}/admin/products/custom/${productId}`, {
        headers: { "x-admin-password": token },
      });
      setCustomProducts((prev) => prev.filter((p) => p.id !== productId));
      setOverrides((prev) => {
        const next = { ...prev };
        delete next[productId];
        return next;
      });
      invalidateProductsCache();
      toast.success("Product deleted");
    } catch {
      toast.error("Could not delete product");
    }
  };

  const savePage = async (slug, payload) => {
    try {
      const res = await axios.put(`${API}/admin/pages/${slug}`, payload, {
        headers: { "x-admin-password": token },
      });
      setPages((prev) => {
        const exists = prev.find((p) => p.slug === res.data.slug);
        if (exists) {
          return prev.map((p) => (p.slug === res.data.slug ? { slug: res.data.slug, title: res.data.title, eyebrow: res.data.eyebrow } : p));
        }
        return [...prev, { slug: res.data.slug, title: res.data.title, eyebrow: res.data.eyebrow }];
      });
      toast.success("Page saved");
      return res.data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save page");
      return null;
    }
  };

  const deletePage = async (slug) => {
    if (!window.confirm(`Delete page "${slug}" permanently?`)) return;
    try {
      await axios.delete(`${API}/admin/pages/${slug}`, {
        headers: { "x-admin-password": token },
      });
      setPages((prev) => prev.filter((p) => p.slug !== slug));
      toast.success("Page deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete page");
    }
  };

  const saveSettings = async (payload) => {
    try {
      const res = await axios.put(`${API}/admin/settings`, payload, {
        headers: { "x-admin-password": token },
      });
      setSettings(res.data || {});
      invalidateSettingsCache();
      toast.success("Settings saved");
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save settings");
      return false;
    }
  };

  const saveCategoryImage = async (categoryId, url) => {
    try {
      await axios.put(
        `${API}/admin/categories/${categoryId}/image`,
        { url },
        { headers: { "x-admin-password": token } },
      );
      setCategoryImages((prev) => ({ ...prev, [categoryId]: url }));
      invalidateCategoryImagesCache();
      toast.success("Category image updated");
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update category image");
      return false;
    }
  };

  const resetCategoryImage = async (categoryId) => {
    if (!window.confirm("Reset this category image to the default?")) return;
    try {
      await axios.delete(`${API}/admin/categories/${categoryId}/image`, {
        headers: { "x-admin-password": token },
      });
      setCategoryImages((prev) => {
        const next = { ...prev };
        delete next[categoryId];
        return next;
      });
      invalidateCategoryImagesCache();
      toast.success("Category image reset");
    } catch {
      toast.error("Could not reset category image");
    }
  };

  const login = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoggingIn(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      toast.success("Welcome back");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid password");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setPassword("");
    setOrders([]);
  };

  const deleteOrder = async (id) => {
    if (!window.confirm("Delete this order permanently?")) return;
    try {
      await axios.delete(`${API}/admin/orders/${id}`, {
        headers: { "x-admin-password": token },
      });
      setOrders((prev) => prev.filter((o) => o.id !== id));
      toast.success("Order deleted");
    } catch {
      toast.error("Could not delete order");
    }
  };

  // ---------- LOGIN VIEW ----------
  if (!token) {
    return (
      <div className="min-h-screen bg-[#050505] text-white relative overflow-hidden flex items-center justify-center px-4">
        <div className="absolute inset-0 bg-grid-dots opacity-30" />
        <div className="absolute inset-0 bg-gradient-radial from-cyan-500/10 via-transparent to-transparent" />
        <div className="relative max-w-md w-full">
          <button
            onClick={() => navigate("/")}
            data-testid="admin-back-btn"
            className="absolute -top-12 left-0 inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-cyan-400 uppercase tracking-wider"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to site
          </button>
          <div className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-8 shadow-[0_0_50px_rgba(0,240,255,0.08)]">
            <div className="flex items-center gap-2.5 mb-6">
              <div className="w-10 h-10 rounded-md bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center shadow-[0_0_15px_rgba(0,240,255,0.4)]">
                <Atom className="w-6 h-6 text-black" strokeWidth={2.5} />
              </div>
              <div className="leading-none">
                <div className="font-headline font-black text-xl">SUPREME</div>
                <div className="text-[10px] tracking-[0.3em] text-cyan-400 font-semibold">CONTROL ROOM</div>
              </div>
            </div>
            <p className="text-xs uppercase tracking-[0.25em] text-cyan-400 font-semibold mb-1">Restricted</p>
            <h1 className="font-headline text-3xl font-black mb-2">Admin Access</h1>
            <p className="text-sm text-zinc-500 mb-6">Sign in to review and manage incoming orders.</p>
            <form onSubmit={login} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="pwd" className="text-xs uppercase tracking-wider text-zinc-400">
                  Admin Password
                </Label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <Input
                    id="pwd"
                    data-testid="admin-password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="bg-[#050505] border-white/10 pl-10 focus:border-cyan-500 focus-visible:ring-cyan-500/30 text-white"
                  />
                </div>
              </div>
              <Button
                type="submit"
                data-testid="admin-login-btn"
                disabled={loggingIn}
                className="w-full h-11 bg-cyan-500 hover:bg-cyan-400 text-black font-bold uppercase tracking-wider shadow-[0_0_20px_rgba(0,240,255,0.4)]"
              >
                {loggingIn ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Authenticating</>
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>
            <div className="mt-6 flex items-start gap-2 bg-yellow-500/5 border border-yellow-500/15 rounded-lg px-3 py-2.5">
              <ShieldAlert className="w-4 h-4 text-yellow-400 mt-0.5 shrink-0" />
              <p className="text-[11px] text-zinc-400 leading-relaxed">
                For MVP demo, default password is <span className="text-yellow-400 font-mono">admin123</span>.
                Change it via the <span className="font-mono">ADMIN_PASSWORD</span> env variable.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------- DASHBOARD VIEW ----------
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/5 bg-[#0A0A0A] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-md bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center shadow-[0_0_15px_rgba(0,240,255,0.4)]">
              <Atom className="w-5 h-5 text-black" strokeWidth={2.5} />
            </div>
            <div className="leading-none">
              <div className="font-headline font-black text-base">SUPREME</div>
              <div className="text-[9px] tracking-[0.3em] text-cyan-400 font-semibold">CONTROL ROOM</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { fetchOrders(token); fetchOverrides(); fetchCustomProducts(); fetchPages(); fetchSettings(); fetchCategoryImages(); }}
              disabled={loading}
              data-testid="admin-refresh-btn"
              className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-cyan-400 font-semibold"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={() => navigate("/")}
              className="bg-white/5 border border-white/10 hover:bg-white/10 text-white font-semibold"
              data-testid="admin-home-btn"
            >
              Site
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              data-testid="admin-logout-btn"
              className="bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 font-semibold"
            >
              <LogOut className="w-3.5 h-3.5 mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* TABS */}
        <div className="flex gap-2 mb-8 border-b border-white/5 overflow-x-auto no-scrollbar -mx-6 px-6">
          <TabButton
            id="orders"
            active={activeTab === "orders"}
            onClick={() => setActiveTab("orders")}
            icon={Inbox}
            label="Orders"
            count={orders.length}
          />
          <TabButton
            id="products"
            active={activeTab === "products"}
            onClick={() => setActiveTab("products")}
            icon={Boxes}
            label="Products"
            count={RAW_PRODUCTS.length + customProducts.length}
          />
          <TabButton
            id="pages"
            active={activeTab === "pages"}
            onClick={() => setActiveTab("pages")}
            icon={FileText}
            label="Pages"
            count={pages.length}
          />
          <TabButton
            id="categories"
            active={activeTab === "categories"}
            onClick={() => setActiveTab("categories")}
            icon={Layers}
            label="Categories"
          />
          <TabButton
            id="settings"
            active={activeTab === "settings"}
            onClick={() => setActiveTab("settings")}
            icon={SettingsIcon}
            label="Settings"
          />
        </div>

        {activeTab === "products" ? (
          <ProductsAdmin
            products={RAW_PRODUCTS}
            customProducts={customProducts}
            overrides={overrides}
            onSave={saveProductOverride}
            onReset={resetProductOverride}
            onCreateCustom={createCustomProduct}
            onUpdateCustom={updateCustomProduct}
            onDeleteCustom={deleteCustomProduct}
            onSetHidden={setProductHidden}
            token={token}
          />
        ) : activeTab === "pages" ? (
          <PagesAdmin
            pages={pages}
            token={token}
            onSave={savePage}
            onDelete={deletePage}
          />
        ) : activeTab === "categories" ? (
          <CategoriesAdmin
            images={categoryImages}
            token={token}
            onSave={saveCategoryImage}
            onReset={resetCategoryImage}
          />
        ) : activeTab === "settings" ? (
          <SettingsAdmin
            initial={settings}
            onSave={saveSettings}
          />
        ) : (
          <OrdersView
            orders={orders}
            loading={loading}
            onDelete={deleteOrder}
          />
        )}
      </main>
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, count }) {
  return (
    <button
      onClick={onClick}
      data-testid={`admin-tab-${label.toLowerCase()}`}
      className={`relative inline-flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.18em] font-bold transition shrink-0 ${
        active ? "text-cyan-400" : "text-zinc-500 hover:text-white"
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
      {count !== undefined && (
        <span className="text-[10px] text-zinc-600 font-mono">({count})</span>
      )}
      {active && (
        <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-cyan-400 shadow-[0_0_8px_#00F0FF]" />
      )}
    </button>
  );
}

function OrdersView({ orders, loading, onDelete }) {
  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-cyan-400 font-semibold mb-2">Live Feed</p>
          <h1 className="font-headline text-4xl font-black tracking-tighter">
            Order <span className="text-cyan-400 glow-cyan">Inbox</span>
          </h1>
          <p className="text-zinc-500 mt-1 text-sm">{orders.length} total order{orders.length === 1 ? "" : "s"}</p>
        </div>
        <div className="flex gap-3">
          <StatCard label="Total" value={orders.length} color="cyan" />
          <StatCard label="Pending" value={orders.filter(o => o.status === "pending").length} color="green" />
        </div>
      </div>

      {loading && orders.length === 0 ? (
        <div className="flex items-center justify-center py-20 text-zinc-500">
          <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading orders...
        </div>
      ) : orders.length === 0 ? (
        <div className="border border-white/5 bg-[#0A0A0A] rounded-xl p-16 text-center" data-testid="admin-empty">
          <Package className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
          <h3 className="font-headline text-xl font-bold text-white mb-1">No orders yet</h3>
          <p className="text-zinc-500 text-sm">When customers submit orders, they will appear here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="admin-orders-list">
          {orders.map((o) => (
            <article
              key={o.id}
              data-testid="admin-order-card"
              className="bg-[#0A0A0A] border border-white/10 hover:border-cyan-500/30 rounded-xl p-5 transition card-glow"
            >
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <div className="font-headline font-bold text-lg text-white">
                    {o.first_name} {o.last_name}
                  </div>
                  <code className="text-[10px] text-zinc-600 font-mono">{o.id}</code>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Badge className="bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 uppercase tracking-wider text-[10px] font-bold">
                    {o.status}
                  </Badge>
                  <span className="text-[10px] text-zinc-600">
                    {new Date(o.created_at).toLocaleString()}
                  </span>
                </div>
              </div>

              {o.product_name && (
                <div className="bg-[#050505] border border-white/5 rounded-lg p-3 mb-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">Product</div>
                      <div className="font-semibold text-white text-sm">{o.product_name}</div>
                      <div className="text-[11px] text-zinc-500 uppercase">{o.product_category}</div>
                    </div>
                    {o.product_price ? (
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500">Price</div>
                        <div className="font-headline font-black text-[#39FF14]">€{o.product_price}</div>
                      </div>
                    ) : null}
                  </div>
                </div>
              )}

              <div className="space-y-1.5 text-sm">
                <div className="flex items-center gap-2 text-zinc-300">
                  <Mail className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                  <a href={`mailto:${o.email}`} className="hover:text-cyan-400 break-all">{o.email}</a>
                </div>
                <div className="flex items-center gap-2 text-zinc-300">
                  <Phone className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                  <a href={`tel:${o.phone}`} className="hover:text-cyan-400">{o.phone}</a>
                </div>
                <div className="flex items-start gap-2 text-zinc-300">
                  <MapPin className="w-3.5 h-3.5 text-cyan-400 shrink-0 mt-0.5" />
                  <span>{o.address}</span>
                </div>
                {o.notes && (
                  <div className="mt-3 pt-3 border-t border-white/5">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Notes</div>
                    <p className="text-zinc-400 text-xs leading-relaxed">{o.notes}</p>
                  </div>
                )}
              </div>

              <div className="flex gap-2 mt-4 pt-4 border-t border-white/5">
                <Button
                  size="sm"
                  onClick={() => onDelete(o.id)}
                  data-testid="admin-delete-order-btn"
                  className="bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 text-xs font-semibold"
                >
                  <Trash2 className="w-3 h-3 mr-1" /> Delete
                </Button>
              </div>
            </article>
          ))}
        </div>
      )}
    </>
  );
}

function ProductsAdmin({ products, customProducts, overrides, onSave, onReset, onCreateCustom, onUpdateCustom, onDeleteCustom, onSetHidden, token }) {
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [showHidden, setShowHidden] = useState(false);

  // Normalize custom products to match the static catalog shape
  const customAsCatalog = customProducts.map((c) => ({
    id: c.id,
    name: c.name,
    category: c.category,
    extraCategories: c.extra_categories || [],
    price: c.price,
    strength: c.strength || "",
    volume: c.volume || "",
    description: c.description || "",
    longDescription: c.long_description || "",
    tags: c.tags || [],
    photo: c.photo || "",
    _custom: true,
  }));

  const combined = [...customAsCatalog, ...products];

  const filtered = combined.filter((p) => {
    if (filterCat && p.category !== filterCat) return false;
    if (!showHidden && overrides[p.id]?.hidden) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return p.name.toLowerCase().includes(q) || p.id.toLowerCase().includes(q);
  });

  const hiddenCount = Object.values(overrides).filter((o) => o?.hidden).length;

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-cyan-400 font-semibold mb-2">Catalog Editor</p>
          <h1 className="font-headline text-4xl font-black tracking-tighter">
            Manage <span className="text-cyan-400 glow-cyan">Products</span>
          </h1>
          <p className="text-zinc-500 mt-1 text-sm">
            Edit price, description and photos. Add brand-new products. Changes are visible to customers instantly.
          </p>
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          data-testid="admin-product-create-btn"
          className="bg-[#39FF14] hover:bg-[#2adc0c] text-black font-bold uppercase tracking-wider text-xs h-10 shadow-[0_0_15px_rgba(57,255,20,0.35)]"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Add new product
        </Button>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            data-testid="admin-product-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products by name or ID..."
            className="bg-[#0A0A0A] border-white/10 pl-10 text-white"
          />
        </div>
        <select
          value={filterCat}
          onChange={(e) => setFilterCat(e.target.value)}
          data-testid="admin-product-cat-filter"
          className="bg-[#0A0A0A] border border-white/10 rounded-md px-3 text-white text-sm h-10 uppercase tracking-wider"
        >
          <option value="">All categories</option>
          {CATEGORIES.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => setShowHidden((v) => !v)}
          data-testid="admin-product-toggle-hidden"
          className={`inline-flex items-center gap-1.5 h-10 px-3 rounded-md border text-[11px] uppercase tracking-[0.15em] font-bold transition ${
            showHidden
              ? "bg-red-500/10 border-red-500/30 text-red-300"
              : "bg-white/5 border-white/10 text-zinc-400 hover:text-white"
          }`}
        >
          {showHidden ? "Hide hidden" : "Show hidden"} · {hiddenCount}
        </button>
      </div>

      <div className="space-y-3" data-testid="admin-products-list">
        {filtered.length === 0 ? (
          <div className="border border-white/5 bg-[#0A0A0A] rounded-xl p-12 text-center">
            <p className="text-zinc-500 text-sm">No products match your filter.</p>
          </div>
        ) : (
          filtered.map((p) => (
            <ProductEditorRow
              key={p.id}
              product={p}
              override={overrides[p.id]}
              onSave={onSave}
              onReset={onReset}
              onDeleteCustom={onDeleteCustom}
              onUpdateCustom={onUpdateCustom}
              onSetHidden={onSetHidden}
              token={token}
            />
          ))
        )}
      </div>

      {showCreate && (
        <CreateProductDialog
          onClose={() => setShowCreate(false)}
          onCreate={onCreateCustom}
          token={token}
        />
      )}
    </>
  );
}

function ProductEditorRow({ product, override, onSave, onReset, onDeleteCustom, onUpdateCustom, onSetHidden, token }) {
  const merged = {
    price: override?.price ?? product.price,
    description: override?.description ?? product.description,
    long_description: override?.long_description ?? product.longDescription,
    photo: override?.photo ?? product.photo,
  };
  const [price, setPrice] = useState(String(merged.price));
  const [description, setDescription] = useState(merged.description || "");
  const [longDesc, setLongDesc] = useState(merged.long_description || "");
  const [photo, setPhoto] = useState(merged.photo || "");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  const isCustom = Boolean(product._custom);
  const hasOverride = Boolean(override);
  const isHidden = Boolean(override?.hidden);
  const dirty =
    Number(price) !== merged.price ||
    description !== (merged.description || "") ||
    longDesc !== (merged.long_description || "") ||
    photo !== (merged.photo || "");

  const uploadMainPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Max file size is 8 MB");
      return;
    }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/upload`, fd, {
        headers: { "x-admin-password": token },
      });
      setPhoto(res.data.url);
      toast.success("Photo uploaded — click Save to apply");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingPhoto(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    const numericPrice = Number(price);
    if (!Number.isFinite(numericPrice) || numericPrice < 0) {
      return;
    }
    setSaving(true);
    let ok;
    if (isCustom) {
      ok = await onUpdateCustom(product.id, {
        price: numericPrice,
        description,
        long_description: longDesc,
        photo: photo || null,
      });
    } else {
      ok = await onSave(product.id, {
        price: numericPrice,
        description,
        long_description: longDesc,
        photo: photo || null,
      });
    }
    setSaving(false);
    if (ok) setOpen(false);
  };

  return (
    <article
      data-testid="admin-product-row"
      className={`bg-[#0A0A0A] border rounded-xl overflow-hidden transition ${
        isCustom ? "border-[#39FF14]/30" : hasOverride ? "border-cyan-500/30" : "border-white/10"
      }`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="admin-product-toggle"
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-white/5 transition"
      >
        <div className="flex items-center gap-3 min-w-0">
          <img
            src={photoUrl(merged.photo)}
            alt={product.name}
            className="w-12 h-12 rounded-md object-contain bg-black border border-white/10 shrink-0"
          />
          <div className="text-left min-w-0">
            <div className="font-semibold text-white text-sm truncate">{product.name}</div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider">
              {product.category} {product.strength ? `· ${product.strength}` : ""}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {isHidden && (
            <Badge className="bg-red-500/10 text-red-300 border border-red-500/30 text-[10px] uppercase tracking-wider font-bold">
              Hidden
            </Badge>
          )}
          {isCustom && (
            <Badge className="bg-[#39FF14]/10 text-[#39FF14] border border-[#39FF14]/30 text-[10px] uppercase tracking-wider font-bold">
              Custom
            </Badge>
          )}
          {!isCustom && hasOverride && !isHidden && (
            <Badge className="bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 text-[10px] uppercase tracking-wider font-bold">
              Edited
            </Badge>
          )}
          <div className="font-headline font-black text-lg text-[#39FF14] tabular-nums">
            €{merged.price}
          </div>
        </div>
      </button>

      {open && (
        <div className="p-5 border-t border-white/5 grid md:grid-cols-[200px_1fr] gap-4 bg-[#070707]">
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Main photo</Label>
              <div className="mt-1.5 relative w-full aspect-square rounded-md overflow-hidden border border-white/10 bg-black">
                <img src={photoUrl(photo)} alt="" className="w-full h-full object-contain" />
                {uploadingPhoto && (
                  <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-[#39FF14]" />
                  </div>
                )}
              </div>
              <label
                data-testid="admin-product-main-photo-label"
                className="mt-2 inline-flex items-center justify-center w-full gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#39FF14] bg-[#39FF14]/[0.05] border border-[#39FF14]/40 hover:border-[#39FF14] rounded-md px-2 py-2 cursor-pointer transition"
              >
                <ImagePlus className="w-3.5 h-3.5" /> Replace photo
                <input
                  type="file"
                  accept="image/*"
                  onChange={uploadMainPhoto}
                  data-testid="admin-product-main-photo-input"
                  className="hidden"
                />
              </label>
              {!isCustom && (
                <p className="text-[10px] text-zinc-600 mt-1.5 leading-relaxed">
                  Default <span className="font-mono text-zinc-500">{product.photo}</span>
                </p>
              )}
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Price (€)</Label>
              <Input
                data-testid="admin-product-price-input"
                type="number"
                step="0.01"
                min="0"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="bg-[#0A0A0A] border-white/10 text-white mt-1.5"
              />
              {!isCustom && (
                <p className="text-[10px] text-zinc-600 mt-1.5">
                  Default <span className="font-mono">€{product.price}</span>
                </p>
              )}
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Short description</Label>
              <Textarea
                data-testid="admin-product-desc-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="bg-[#0A0A0A] border-white/10 text-white mt-1.5 text-sm"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Long description</Label>
              <Textarea
                data-testid="admin-product-long-input"
                value={longDesc}
                onChange={(e) => setLongDesc(e.target.value)}
                rows={4}
                className="bg-[#0A0A0A] border-white/10 text-white mt-1.5 text-sm"
              />
            </div>
            <ProductGalleryEditor productId={product.id} gallery={override?.gallery || []} />
            <div className="flex justify-end gap-2 pt-2">
              {isCustom ? (
                <Button
                  size="sm"
                  onClick={() => onDeleteCustom(product.id)}
                  data-testid="admin-product-delete-custom-btn"
                  className="bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 text-xs font-semibold"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete product
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={() => {
                    if (isHidden) {
                      onSetHidden(product.id, false);
                    } else if (
                      window.confirm(
                        "Hide this product from the catalog? Customers won't see it. You can restore it any time from the 'Show hidden' filter.",
                      )
                    ) {
                      onSetHidden(product.id, true);
                    }
                  }}
                  data-testid="admin-product-hide-btn"
                  className={`text-xs font-semibold ${
                    isHidden
                      ? "bg-[#39FF14]/10 hover:bg-[#39FF14]/20 text-[#39FF14] border border-[#39FF14]/30"
                      : "bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20"
                  }`}
                >
                  {isHidden ? (
                    <><RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Restore</>
                  ) : (
                    <><Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete from site</>
                  )}
                </Button>
              )}
              {!isCustom && hasOverride && (
                <Button
                  size="sm"
                  onClick={() => onReset(product.id)}
                  data-testid="admin-product-reset-btn"
                  className="bg-white/5 border border-white/10 text-zinc-300 hover:text-white hover:bg-white/10 text-xs font-semibold"
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset to default
                </Button>
              )}
              <Button
                size="sm"
                onClick={save}
                disabled={!dirty || saving}
                data-testid="admin-product-save-btn"
                className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold uppercase tracking-wider text-xs disabled:opacity-50"
              >
                {saving ? (
                  <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Saving</>
                ) : (
                  <><Save className="w-3.5 h-3.5 mr-1.5" /> Save changes</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </article>
  );
}

function CreateProductDialog({ onClose, onCreate, token }) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState(CATEGORIES[0]?.id || "");
  const [price, setPrice] = useState("");
  const [strength, setStrength] = useState("");
  const [volume, setVolume] = useState("");
  const [description, setDescription] = useState("");
  const [longDesc, setLongDesc] = useState("");
  const [photo, setPhoto] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Max file size is 8 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/upload`, fd, {
        headers: { "x-admin-password": token },
      });
      setPhoto(res.data.url);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const numericPrice = Number(price);
    if (!name.trim() || !Number.isFinite(numericPrice) || numericPrice < 0) {
      toast.error("Name and a valid price are required");
      return;
    }
    setSubmitting(true);
    const ok = await onCreate({
      name: name.trim(),
      category,
      price: numericPrice,
      strength: strength.trim(),
      volume: volume.trim(),
      description: description.trim(),
      long_description: longDesc.trim(),
      photo: photo || null,
    });
    setSubmitting(false);
    if (ok) onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
      data-testid="admin-product-create-dialog"
    >
      <form
        onSubmit={submit}
        className="bg-[#0A0A0A] border border-[#39FF14]/30 rounded-2xl w-full max-w-2xl my-8 shadow-[0_0_60px_rgba(57,255,20,0.15)]"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#39FF14] font-bold">New Product</p>
            <h2 className="font-headline text-xl font-black">Add to catalog</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="admin-product-create-close"
            className="w-8 h-8 rounded-md bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-400"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 grid md:grid-cols-[180px_1fr] gap-5">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Main photo</Label>
            <div className="mt-1.5 relative w-full aspect-square rounded-md overflow-hidden border border-white/10 bg-black flex items-center justify-center">
              {photo ? (
                <img src={photoUrl(photo)} alt="" className="w-full h-full object-contain" />
              ) : (
                <ImagePlus className="w-8 h-8 text-zinc-700" />
              )}
              {uploading && (
                <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-[#39FF14]" />
                </div>
              )}
            </div>
            <label className="mt-2 inline-flex items-center justify-center w-full gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#39FF14] bg-[#39FF14]/[0.05] border border-[#39FF14]/40 hover:border-[#39FF14] rounded-md px-2 py-2 cursor-pointer transition">
              <ImagePlus className="w-3.5 h-3.5" /> Upload photo
              <input
                type="file"
                accept="image/*"
                onChange={uploadPhoto}
                data-testid="admin-product-create-photo-input"
                className="hidden"
              />
            </label>
          </div>

          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Name *</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. DSIP Nasal Spray 10 mg"
                data-testid="admin-product-create-name"
                className="bg-[#050505] border-white/10 text-white mt-1.5"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Category *</Label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  data-testid="admin-product-create-category"
                  className="bg-[#050505] border border-white/10 rounded-md px-3 text-white text-sm h-10 w-full mt-1.5 uppercase tracking-wider"
                  required
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Price (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="125"
                  data-testid="admin-product-create-price"
                  className="bg-[#050505] border-white/10 text-white mt-1.5"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Strength</Label>
                <Input
                  value={strength}
                  onChange={(e) => setStrength(e.target.value)}
                  placeholder="e.g. 10 mg"
                  data-testid="admin-product-create-strength"
                  className="bg-[#050505] border-white/10 text-white mt-1.5"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Volume / format</Label>
                <Input
                  value={volume}
                  onChange={(e) => setVolume(e.target.value)}
                  placeholder="e.g. Nasal Spray 10 ml"
                  data-testid="admin-product-create-volume"
                  className="bg-[#050505] border-white/10 text-white mt-1.5"
                />
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Short description</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                data-testid="admin-product-create-desc"
                className="bg-[#050505] border-white/10 text-white mt-1.5 text-sm"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Long description</Label>
              <Textarea
                value={longDesc}
                onChange={(e) => setLongDesc(e.target.value)}
                rows={4}
                data-testid="admin-product-create-long"
                className="bg-[#050505] border-white/10 text-white mt-1.5 text-sm"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-white/5 bg-[#070707]">
          <Button
            type="button"
            onClick={onClose}
            size="sm"
            className="bg-white/5 border border-white/10 text-zinc-300 hover:text-white hover:bg-white/10 text-xs font-semibold"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={submitting || uploading}
            data-testid="admin-product-create-submit"
            className="bg-[#39FF14] hover:bg-[#2adc0c] text-black font-bold uppercase tracking-wider text-xs disabled:opacity-50"
          >
            {submitting ? (
              <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Creating</>
            ) : (
              <><Plus className="w-3.5 h-3.5 mr-1.5" /> Create product</>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}

function ProductGalleryEditor({ productId, gallery }) {
  const [uploading, setUploading] = useState(false);
  const [items, setItems] = useState(gallery);
  useEffect(() => setItems(gallery), [gallery]);
  const token = localStorage.getItem(TOKEN_KEY) || "";

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Max file size is 8 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/products/${productId}/images`, fd, {
        headers: { "x-admin-password": token },
      });
      setItems(res.data.gallery);
      invalidateProductsCache();
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDelete = async (url) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      const res = await axios.delete(`${API}/admin/products/${productId}/images`, {
        headers: { "x-admin-password": token },
        params: { url },
      });
      setItems(res.data.gallery);
      invalidateProductsCache();
      toast.success("Image removed");
    } catch {
      toast.error("Could not delete image");
    }
  };

  return (
    <div data-testid="admin-product-gallery-editor">
      <Label className="text-[10px] uppercase tracking-wider text-zinc-400">
        Gallery images <span className="text-zinc-600 normal-case">(COA, lab photos…)</span>
      </Label>
      <div className="mt-2 flex flex-wrap gap-2">
        {items.map((url) => {
          const fullUrl = url.startsWith("http")
            ? url
            : `${process.env.REACT_APP_BACKEND_URL}${url}`;
          return (
            <div
              key={url}
              data-testid="admin-gallery-thumb"
              className="relative w-20 h-20 rounded-md overflow-hidden border border-white/10 group"
            >
              <img src={fullUrl} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => handleDelete(url)}
                data-testid="admin-gallery-delete"
                aria-label="Delete image"
                className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center opacity-0 group-hover:opacity-100 transition shadow"
              >
                ×
              </button>
            </div>
          );
        })}
        <label
          data-testid="admin-gallery-upload-label"
          className={`relative w-20 h-20 rounded-md border-2 border-dashed border-[#39FF14]/40 hover:border-[#39FF14] flex flex-col items-center justify-center text-[#39FF14] text-[10px] uppercase tracking-wider font-bold cursor-pointer bg-[#39FF14]/[0.04] transition ${
            uploading ? "opacity-60 pointer-events-none" : ""
          }`}
        >
          {uploading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <ImagePlus className="w-5 h-5 mb-1" />
              Add
            </>
          )}
          <input
            type="file"
            accept="image/*"
            onChange={handleUpload}
            data-testid="admin-gallery-upload-input"
            className="absolute inset-0 opacity-0 cursor-pointer"
          />
        </label>
      </div>
    </div>
  );
}


function PagesAdmin({ pages, token, onSave, onDelete }) {
  const [creating, setCreating] = useState(false);

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-cyan-400 font-semibold mb-2">Site Pages</p>
          <h1 className="font-headline text-4xl font-black tracking-tighter">
            Edit <span className="text-cyan-400 glow-cyan">Pages</span>
          </h1>
          <p className="text-zinc-500 mt-1 text-sm">
            Edit Terms, Privacy, Contact or any custom page. Content uses Markdown — headings, links, lists are supported.
          </p>
        </div>
        <Button
          onClick={() => setCreating(true)}
          data-testid="admin-page-create-btn"
          className="bg-[#39FF14] hover:bg-[#2adc0c] text-black font-bold uppercase tracking-wider text-xs h-10 shadow-[0_0_15px_rgba(57,255,20,0.35)]"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Add new page
        </Button>
      </div>

      <div className="space-y-3" data-testid="admin-pages-list">
        {pages.length === 0 ? (
          <div className="border border-white/5 bg-[#0A0A0A] rounded-xl p-12 text-center">
            <p className="text-zinc-500 text-sm">No pages yet. Click "Add new page".</p>
          </div>
        ) : (
          pages.map((p) => (
            <PageEditorRow
              key={p.slug}
              meta={p}
              token={token}
              onSave={onSave}
              onDelete={onDelete}
            />
          ))
        )}
      </div>

      {creating && (
        <PageCreateDialog
          existingSlugs={pages.map((p) => p.slug)}
          onClose={() => setCreating(false)}
          onCreate={async (data) => {
            const ok = await onSave(data.slug, { title: data.title, eyebrow: data.eyebrow, content: data.content });
            if (ok) setCreating(false);
          }}
        />
      )}
    </>
  );
}

const PROTECTED_PAGE_SLUGS = new Set(["terms-and-conditions", "privacy-policy", "contact-us"]);

function PageEditorRow({ meta, token, onSave, onDelete }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(meta.title || "");
  const [eyebrow, setEyebrow] = useState(meta.eyebrow || "");
  const [content, setContent] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const isProtected = PROTECTED_PAGE_SLUGS.has(meta.slug);

  const loadContent = async () => {
    if (loaded) return;
    try {
      const res = await axios.get(`${API}/pages/${meta.slug}`);
      setTitle(res.data.title || "");
      setEyebrow(res.data.eyebrow || "");
      setContent(res.data.content || "");
      setLoaded(true);
    } catch {
      toast.error("Could not load page content");
    }
  };

  const toggle = async () => {
    if (!open) await loadContent();
    setOpen((v) => !v);
  };

  const save = async () => {
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    const res = await onSave(meta.slug, { title: title.trim(), eyebrow: eyebrow.trim(), content });
    setSaving(false);
    if (res) {
      setLoaded(true);
    }
  };

  return (
    <article
      data-testid="admin-page-row"
      className="bg-[#0A0A0A] border border-white/10 rounded-xl overflow-hidden transition"
    >
      <button
        type="button"
        onClick={toggle}
        data-testid="admin-page-toggle"
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-white/5 transition"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-md bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center shrink-0">
            <FileText className="w-4 h-4 text-cyan-400" />
          </div>
          <div className="text-left min-w-0">
            <div className="font-semibold text-white text-sm truncate">{meta.title || meta.slug}</div>
            <div className="text-[10px] text-zinc-500 font-mono">/page/{meta.slug}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isProtected && (
            <Badge className="bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 text-[10px] uppercase tracking-wider font-bold">
              Default
            </Badge>
          )}
          <a
            href={`/page/${meta.slug}`}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-cyan-400 font-semibold"
          >
            View →
          </a>
        </div>
      </button>

      {open && (
        <div className="p-5 border-t border-white/5 space-y-3 bg-[#070707]">
          {!loaded ? (
            <div className="flex items-center gap-2 text-zinc-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading...
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-[200px_1fr] gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Eyebrow</Label>
                  <Input
                    value={eyebrow}
                    onChange={(e) => setEyebrow(e.target.value)}
                    placeholder="e.g. Legal"
                    data-testid="admin-page-eyebrow-input"
                    className="bg-[#0A0A0A] border-white/10 text-white mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Title *</Label>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Page title"
                    data-testid="admin-page-title-input"
                    className="bg-[#0A0A0A] border-white/10 text-white mt-1.5"
                  />
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-400">
                  Content (Markdown)
                </Label>
                <Textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={20}
                  data-testid="admin-page-content-input"
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1.5 text-sm font-mono"
                />
                <p className="text-[10px] text-zinc-600 mt-1.5 leading-relaxed">
                  Use <code className="text-zinc-400 bg-white/5 px-1 rounded">## Heading</code>,
                  {" "}<code className="text-zinc-400 bg-white/5 px-1 rounded">**bold**</code>,
                  {" "}<code className="text-zinc-400 bg-white/5 px-1 rounded">[link](/url)</code>,
                  {" "}<code className="text-zinc-400 bg-white/5 px-1 rounded">- list item</code>.
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                {!isProtected && (
                  <Button
                    size="sm"
                    onClick={() => onDelete(meta.slug)}
                    data-testid="admin-page-delete-btn"
                    className="bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 text-xs font-semibold"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete page
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={save}
                  disabled={saving}
                  data-testid="admin-page-save-btn"
                  className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold uppercase tracking-wider text-xs disabled:opacity-50"
                >
                  {saving ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Saving</>
                  ) : (
                    <><Save className="w-3.5 h-3.5 mr-1.5" /> Save page</>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </article>
  );
}

function PageCreateDialog({ existingSlugs, onClose, onCreate }) {
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [eyebrow, setEyebrow] = useState("");
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const slugFromTitle = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

  const submit = async (e) => {
    e.preventDefault();
    const finalSlug = (slug || slugFromTitle(title)).trim();
    if (!title.trim() || !finalSlug) {
      toast.error("Title and slug are required");
      return;
    }
    if (existingSlugs.includes(finalSlug)) {
      toast.error("A page with this slug already exists");
      return;
    }
    setSubmitting(true);
    await onCreate({ slug: finalSlug, title: title.trim(), eyebrow: eyebrow.trim(), content });
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="admin-page-create-dialog">
      <form
        onSubmit={submit}
        className="bg-[#0A0A0A] border border-cyan-500/30 rounded-2xl w-full max-w-2xl my-8 shadow-[0_0_60px_rgba(0,240,255,0.15)]"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-cyan-400 font-bold">New Page</p>
            <h2 className="font-headline text-xl font-black">Create a page</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="admin-page-create-close"
            className="w-8 h-8 rounded-md bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-400"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Title *</Label>
              <Input
                value={title}
                onChange={(e) => { setTitle(e.target.value); if (!slug) setSlug(slugFromTitle(e.target.value)); }}
                placeholder="e.g. FAQ"
                data-testid="admin-page-create-title"
                className="bg-[#050505] border-white/10 text-white mt-1.5"
                required
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Slug (URL)</Label>
              <Input
                value={slug}
                onChange={(e) => setSlug(slugFromTitle(e.target.value))}
                placeholder="auto"
                data-testid="admin-page-create-slug"
                className="bg-[#050505] border-white/10 text-white mt-1.5"
              />
              <p className="text-[10px] text-zinc-600 mt-1">URL will be <code className="text-zinc-500">/page/{slug || "{slug}"}</code></p>
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Eyebrow (small label above title)</Label>
            <Input
              value={eyebrow}
              onChange={(e) => setEyebrow(e.target.value)}
              placeholder="e.g. Help Center"
              data-testid="admin-page-create-eyebrow"
              className="bg-[#050505] border-white/10 text-white mt-1.5"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Content (Markdown)</Label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={10}
              placeholder={"## Section heading\n\nYour paragraph here.\n\n- Bullet\n- Bullet"}
              data-testid="admin-page-create-content"
              className="bg-[#050505] border-white/10 text-white mt-1.5 text-sm font-mono"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-white/5 bg-[#070707]">
          <Button type="button" onClick={onClose} size="sm" className="bg-white/5 border border-white/10 text-zinc-300 hover:text-white hover:bg-white/10 text-xs font-semibold">
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={submitting}
            data-testid="admin-page-create-submit"
            className="bg-[#39FF14] hover:bg-[#2adc0c] text-black font-bold uppercase tracking-wider text-xs disabled:opacity-50"
          >
            {submitting ? (
              <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Creating</>
            ) : (
              <><Plus className="w-3.5 h-3.5 mr-1.5" /> Create page</>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}


function CategoriesAdmin({ images, token, onSave, onReset }) {
  return (
    <>
      <div className="mb-8">
        <p className="text-[11px] uppercase tracking-[0.3em] text-cyan-400 font-bold mb-2">Category images</p>
        <h2 className="text-4xl font-headline font-black">
          <span className="text-white">Manage </span>
          <span className="text-cyan-400">Categories</span>
        </h2>
        <p className="text-sm text-zinc-500 mt-2">
          Upload a custom photo for any category card shown on the Home and Products pages.
          Recommended: square image, at least 800×800 px.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {CATEGORIES.map((cat) => (
          <CategoryImageCard
            key={cat.id}
            category={cat}
            currentUrl={images[cat.id]}
            token={token}
            onSave={onSave}
            onReset={onReset}
          />
        ))}
      </div>
    </>
  );
}


function CategoryImageCard({ category, currentUrl, token, onSave, onReset }) {
  const [uploading, setUploading] = useState(false);

  const previewSrc = resolveCategoryImage(category.id, currentUrl ? { [category.id]: currentUrl } : {});
  const displaySrc = previewSrc.startsWith("/api/")
    ? `${process.env.REACT_APP_BACKEND_URL}${previewSrc}`
    : previewSrc;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(`${API}/admin/upload`, form, {
        headers: {
          "x-admin-password": token,
          "Content-Type": "multipart/form-data",
        },
      });
      const url = res.data?.url;
      if (url) {
        await onSave(category.id, url);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="bg-[#0A0A0A] border border-white/10 rounded-xl overflow-hidden">
      <div className="relative w-full aspect-square bg-black overflow-hidden">
        <img
          src={displaySrc}
          alt={category.name}
          className="absolute inset-0 w-full h-full object-cover"
        />
        {currentUrl && (
          <div className="absolute top-2 right-2 bg-[#39FF14]/20 border border-[#39FF14]/40 text-[#39FF14] text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded">
            Custom
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-bold text-white text-sm uppercase tracking-wider">{category.name}</h3>
        <p className="text-xs text-zinc-500 mt-1">{category.sub}</p>

        <div className="flex gap-2 mt-4">
          <label
            className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#39FF14]/10 hover:bg-[#39FF14]/20 border border-[#39FF14]/30 text-[#39FF14] text-xs font-bold uppercase tracking-wider px-3 py-2 rounded cursor-pointer transition disabled:opacity-50"
            data-testid={`admin-category-upload-${category.id}`}
          >
            {uploading ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading</>
            ) : (
              <><ImagePlus className="w-3.5 h-3.5" /> {currentUrl ? "Replace" : "Upload"}</>
            )}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFile}
              disabled={uploading}
            />
          </label>
          {currentUrl && (
            <button
              onClick={() => onReset(category.id)}
              data-testid={`admin-category-reset-${category.id}`}
              className="inline-flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-300 hover:text-white text-xs font-bold uppercase tracking-wider px-3 py-2 rounded transition"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


function SettingsAdmin({ initial, onSave }) {
  const [supreme, setSupreme] = useState(initial.whatsapp_supreme || "");
  const [gymgear, setGymgear] = useState(initial.whatsapp_gymgear || "");
  const [telegram, setTelegram] = useState(initial.telegram_username || "");
  const [saving, setSaving] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);

  useEffect(() => {
    setSupreme(initial.whatsapp_supreme || "");
    setGymgear(initial.whatsapp_gymgear || "");
    setTelegram(initial.telegram_username || "");
  }, [initial.whatsapp_supreme, initial.whatsapp_gymgear, initial.telegram_username]);

  const cleanSupreme = supreme.replace(/\D/g, "");
  const cleanGymgear = gymgear.replace(/\D/g, "");
  const cleanTelegram = telegram.trim().replace(/^@/, "");
  const dirty =
    cleanSupreme !== (initial.whatsapp_supreme || "").replace(/\D/g, "") ||
    cleanGymgear !== (initial.whatsapp_gymgear || "").replace(/\D/g, "") ||
    cleanTelegram !== (initial.telegram_username || "").replace(/^@/, "");

  const save = async () => {
    setSaving(true);
    await onSave({
      whatsapp_supreme: cleanSupreme,
      whatsapp_gymgear: cleanGymgear,
      telegram_username: cleanTelegram,
    });
    setSaving(false);
  };

  const runCleanup = async () => {
    if (!window.confirm(
      "This will remove broken photo references (uploaded before the storage migration). Text edits (prices, descriptions) are kept. You will need to re-upload photos that appear broken. Continue?"
    )) return;
    setCleaning(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY) || "";
      const res = await axios.post(
        `${API}/admin/cleanup-broken-photos`,
        {},
        { headers: { "x-admin-password": token || "" } },
      );
      setCleanupResult(res.data);
      invalidateProductsCache();
      toast.success(`Cleared ${res.data.reset_photos} photos and ${res.data.reset_gallery_docs} galleries`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cleanup failed");
    } finally {
      setCleaning(false);
    }
  };

  return (
    <>
      <div className="mb-6">
        <p className="text-xs uppercase tracking-[0.3em] text-cyan-400 font-semibold mb-2">Site Settings</p>
        <h1 className="font-headline text-4xl font-black tracking-tighter">
          General <span className="text-cyan-400 glow-cyan">Settings</span>
        </h1>
        <p className="text-zinc-500 mt-1 text-sm">
          Update contact numbers used by the floating WhatsApp buttons, footer and Shipping page.
        </p>
      </div>

      <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-6 space-y-5 max-w-2xl">
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-zinc-400 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#25D366]" />
            WhatsApp — Supreme Laboratories (main FAB, footer, shipping page)
          </Label>
          <Input
            data-testid="settings-whatsapp-supreme"
            value={supreme}
            onChange={(e) => setSupreme(e.target.value)}
            placeholder="+49 1522 8205868"
            className="bg-[#050505] border-white/10 text-white mt-1.5 font-mono"
          />
          <p className="text-[10px] text-zinc-600 mt-1.5">
            Enter with or without spaces or the "+" sign. Only digits are saved.
            Preview link:{" "}
            <a
              href={`https://wa.me/${cleanSupreme}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#39FF14] hover:underline break-all"
            >
              https://wa.me/{cleanSupreme || "…"}
            </a>
          </p>
        </div>

        <div>
          <Label className="text-[11px] uppercase tracking-wider text-zinc-400 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-500" />
            WhatsApp — Gym Gear (red pill FAB)
          </Label>
          <Input
            data-testid="settings-whatsapp-gymgear"
            value={gymgear}
            onChange={(e) => setGymgear(e.target.value)}
            placeholder="+49 1522 8205868"
            className="bg-[#050505] border-white/10 text-white mt-1.5 font-mono"
          />
          <p className="text-[10px] text-zinc-600 mt-1.5">
            Preview link:{" "}
            <a
              href={`https://wa.me/${cleanGymgear}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#39FF14] hover:underline break-all"
            >
              https://wa.me/{cleanGymgear || "…"}
            </a>
          </p>
        </div>

        <div>
          <Label className="text-[11px] uppercase tracking-wider text-zinc-400 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#229ED9]" />
            Telegram username (order success screen)
          </Label>
          <Input
            data-testid="settings-telegram-username"
            value={telegram}
            onChange={(e) => setTelegram(e.target.value)}
            placeholder="SupremeLaboratories"
            className="bg-[#050505] border-white/10 text-white mt-1.5 font-mono"
          />
          <p className="text-[10px] text-zinc-600 mt-1.5">
            Enter the username with or without the leading "@".
            Preview link:{" "}
            <a
              href={`https://t.me/${cleanTelegram}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#39FF14] hover:underline break-all"
            >
              https://t.me/{cleanTelegram || "…"}
            </a>
          </p>
        </div>

        <div className="flex justify-end pt-2 border-t border-white/5">
          <Button
            onClick={save}
            disabled={!dirty || saving}
            data-testid="settings-save-btn"
            className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold uppercase tracking-wider text-xs disabled:opacity-50"
          >
            {saving ? (
              <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Saving</>
            ) : (
              <><Save className="w-3.5 h-3.5 mr-1.5" /> Save settings</>
            )}
          </Button>
        </div>
      </div>

      {/* Maintenance card */}
      <div className="mt-6 bg-[#0A0A0A] border border-red-500/20 rounded-xl p-6 max-w-2xl">
        <div className="flex items-start gap-3 mb-3">
          <ShieldAlert className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
          <div>
            <h3 className="font-headline text-lg font-bold text-white">Fix broken product photos</h3>
            <p className="text-xs text-zinc-500 leading-relaxed mt-1">
              If some of your uploaded product photos disappeared after a deploy, click below to
              clean up references to files that no longer exist. Text edits (prices, descriptions)
              are preserved. New uploads are now stored inside the database and won&apos;t disappear again.
            </p>
          </div>
        </div>
        <Button
          onClick={runCleanup}
          disabled={cleaning}
          data-testid="settings-cleanup-btn"
          className="bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 text-xs font-semibold uppercase tracking-wider disabled:opacity-50"
        >
          {cleaning ? (
            <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Cleaning</>
          ) : (
            <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Run cleanup</>
          )}
        </Button>
        {cleanupResult && (
          <p className="text-[11px] text-zinc-400 mt-3">
            Cleared <span className="text-red-300 font-bold">{cleanupResult.reset_photos}</span>{" "}
            product photos and{" "}
            <span className="text-red-300 font-bold">{cleanupResult.reset_gallery_docs}</span>{" "}
            galleries. Please re-upload the photos you need.
          </p>
        )}
      </div>
    </>
  );
}


function StatCard({ label, value, color }) {
  const colors = {
    cyan: "text-cyan-400 border-cyan-500/30",
    green: "text-[#39FF14] border-[#39FF14]/30",
  };
  return (
    <div className={`bg-[#0A0A0A] border ${colors[color]} rounded-lg px-4 py-3 min-w-[100px]`}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`font-headline font-black text-2xl ${colors[color].split(" ")[0]}`}>{value}</div>
    </div>
  );
}
